import React, { useState } from 'react';
import globalProgramStore from '../../../../stores/program-store';
import dateUtils from '../../../../utils/date-format';
import { UserSummaryType } from '../../../../api/types/user/user-summary.type';
import { addStudentsToProgramOperation } from './operations/add-students-to-program-operation';
import globalConfigStore from '../../../../stores/global-config-store';
import { Column } from 'primereact/column';
import { DataTable } from 'primereact/datatable';
import { Button } from 'primereact/button';
import { observer } from 'mobx-react-lite';
import { UserType } from '../../../../api/types/user/user-type';
import { Checkbox } from 'primereact/checkbox';
import { CourseMetadataType } from '../../../../api/types/programs/course/course-metadata.type';
import { UserAvatarSmall } from '../../../../component/user-avatar/user-avatar-small';
import { deleteStudentFromProgram } from './operations/delete-student-from-program';
import { Accordion, AccordionTab } from 'primereact/accordion';
import { ProgramStudent } from '../../../../api/types/programs/program-details';
import { getProgramStudentStatusDisplayText } from '../../../../api/types/programs/students/program-student-status';
import { ProgramStudentStatusDialog } from './componenets/program-student-status-dialog';
import { UserPageDialog } from '../../../../component/ui/search-users/user-page-dialog/user-page-dialog';
import { AddStudentsDialog } from './dialogs/add-students-dialog';
import generateAndDownloadExcelFile from '../../../../component/excel/generate-and-download-excel-file';
import globalSettingsBranches from '../../../../stores/global-settings-branches';
import { Tag } from 'primereact/tag';
import { PhoneNumberComponent } from '../../../../utils/international-to-israeli';
import {
  Gender,
  MaritalStatus,
} from '../../../../api/types/user/user-summary.type';
import {
  OutlookMailDialog,
  OutlookMailDialogUser,
} from '../../../../component/outlook-mail-dialog/outlook-mail-dialog';

// Interface for year-based student structure
interface YearStudents {
  year: number;
  yearName: string;
  students: UserSummaryType[];
}

const ProgramStudentsTab = observer(() => {
  const [showAddDialog, setShowAddDialog] = useState(false);
  const [selectedYear, setSelectedYear] = useState<number | null>(null);

  const [selectedUsers, setSelectedUsers] = useState<UserSummaryType[]>([]);
  const [addCourses, setAddCourses] = useState(false);
  const [selectedCourses, setSelectedCourses] = useState<CourseMetadataType[]>(
    [],
  );
  const [deleteStudentId, setDeleteStudentId] = useState<number | null>(null);
  const [showYearView, setShowYearView] = useState(true);
  const [showProgramView, setShowProgramView] = useState(false);
  const [showStatusDialog, setShowStatusDialog] = useState(false);
  const [selectedStudent, setSelectedStudent] = useState<ProgramStudent | null>(
    null,
  );
  const [selectedUserForDialog, setSelectedUserForDialog] = useState<
    UserSummaryType | undefined
  >();
  const [showOutlookDialog, setShowOutlookDialog] = useState(false);
  const [outlookUsers, setOutlookUsers] = useState<OutlookMailDialogUser[]>([]);
  const [outlookTitle, setOutlookTitle] = useState<string>('ייצוא לאאוטלוק');

  const currentProgramId = globalProgramStore.getCurrentId()!;
  const program = globalProgramStore.programs.get(currentProgramId)!;

  // Generate year students from program details years
  const yearStudents: YearStudents[] =
    program.details?.years?.map((yearData) => ({
      year: yearData.year,
      yearName: `שנה ${yearData.year === 0 ? 'א׳' : yearData.year === 1 ? 'ב׳' : 'ג׳'}`,
      students: yearData.students || [],
    })) || [];

  const handleAddStudentClick = (year: number) => {
    setSelectedYear(year);
    setShowAddDialog(true);
  };

  const handleOnApprove = async (
    selectedUsers: UserSummaryType[],
    setLoading: (loading: boolean) => void,
  ) => {
    const branchId = globalConfigStore.rootFilter.currentBranches?.id;
    await addStudentsToProgramOperation(
      branchId,
      globalProgramStore.getCurrentId(),
      selectedYear || 0,
      selectedUsers,
      setShowAddDialog,
      setLoading,
      selectedCourses,
    );
  };

  const handleStudentDelete = async (studentId: number, yearNumber: number) => {
    const branchId = globalConfigStore.rootFilter.currentBranches?.id;
    const currentProgramId = globalProgramStore.getCurrentId();

    await deleteStudentFromProgram(
      branchId,
      currentProgramId,
      yearNumber,
      studentId,
      setDeleteStudentId,
    );
  };

  const handleStatusClick = (student: ProgramStudent) => {
    setSelectedStudent(student);
    setShowStatusDialog(true);
  };

  const handleUserClick = (user: UserSummaryType) => {
    setSelectedUserForDialog(user);
  };

  const closeUserDialog = () => {
    setSelectedUserForDialog(undefined);
  };

  const getGenderDisplayText = (gender: Gender): string => {
    switch (gender) {
      case Gender.Male:
        return 'זכר';
      case Gender.Female:
        return 'נקבה';
      default:
        return '';
    }
  };

  const getMaritalStatusDisplayText = (
    maritalStatus: MaritalStatus,
  ): string => {
    switch (maritalStatus) {
      case MaritalStatus.Single:
        return 'רווק';
      case MaritalStatus.Married:
        return 'נשוי';
      case MaritalStatus.Divorced:
        return 'גרוש';
      case MaritalStatus.Widowed:
        return 'אלמן';
      default:
        return '';
    }
  };

  const toOutlookUsers = (
    students: UserSummaryType[],
  ): OutlookMailDialogUser[] =>
    students.map((s) => ({
      userId: s.id,
      firstName: s.firstName,
      lastName: s.lastName,
      nationalId: s.nationalId,
      mail: s.email,
    }));

  const openOutlookDialog = (students: UserSummaryType[], title: string) => {
    setOutlookUsers(toOutlookUsers(students));
    setOutlookTitle(title);
    setShowOutlookDialog(true);
  };

  const formatAddress = (student: UserSummaryType): string => {
    const parts = [
      student.city,
      student.street,
      student.houseNumber,
      student.flatNumber,
      student.postcode,
    ].filter(Boolean);
    return parts.join(', ') || '';
  };

  const handleExportToExcel = (
    students: UserSummaryType[],
    yearName: string,
  ) => {
    const programName = program.metadata?.programName || 'תוכנית';
    const fileName = `סטודנטים בתוכנית ${programName} ${yearName}`;

    // Create header row
    const headers = ['שם', 'ת.ז', 'נייד', 'מייל', 'כתובת', 'מין', 'מצב משפחתי'];

    // Create data rows
    const dataRows = students.map((student) => [
      `${student.firstName} ${student.lastName}`,
      student.nationalId || '',
      student.phoneNumber || '',
      student.email || '',
      formatAddress(student),
      getGenderDisplayText(student.gender),
      getMaritalStatusDisplayText(student.maritalStatus),
    ]);

    // Combine headers and data
    const excelData = [headers, ...dataRows];

    // Generate and download
    generateAndDownloadExcelFile(excelData);
  };

  const handleExportProgramStudentsToExcel = () => {
    const programName = program.metadata?.programName || 'תוכנית';
    const programStudents = program.details?.programStudents || [];

    // Create header row
    const headers = [
      'שם',
      'ת.ז',
      'סטטוס',
      'תאריך רישום',
      'תאריך תחילת לימודים',
      'כתובת',
      'מין',
      'מצב משפחתי',
    ];

    // Create data rows
    const dataRows = programStudents.map((student) => [
      `${student.firstName} ${student.lastName}`,
      student.nationalId || '',
      getProgramStudentStatusDisplayText(student.status),
      student.registrationDate
        ? dateUtils.displayFormatDate(student.registrationDate)
        : '',
      student.studyingStartDate
        ? dateUtils.displayFormatDate(student.studyingStartDate)
        : '',
      formatAddress(student),
      getGenderDisplayText(student.gender),
      getMaritalStatusDisplayText(student.maritalStatus),
    ]);

    // Combine headers and data
    const excelData = [headers, ...dataRows];

    // Generate and download
    generateAndDownloadExcelFile(excelData);
  };

  const renderYearStudentsTable = (
    students: UserSummaryType[],
    yearNumber: number,
  ) => (
    <DataTable
      value={students}
      scrollable
      emptyMessage={
        <div style={{ textAlign: 'right' }}>לא נמצאו סטודנטים בשנה זו</div>
      }
      dataKey="id"
      tableStyle={{
        direction: 'rtl',
        marginTop: '8px',
      }}
      onRowClick={(e) => handleUserClick(e.data as UserSummaryType)}
      className="clickable-rows"
    >
      <Column
        style={{ textAlign: 'right', width: '50px' }}
        field="lastName"
        header=""
        body={(rowData) => (
          <UserAvatarSmall
            imageUrl={rowData?.fileUrl}
            firstName={rowData?.firstName}
            lastName={rowData?.lastName}
          />
        )}
      />
      <Column
        style={{ textAlign: 'right', width: '150px' }}
        field="firstName"
        header="שם"
        body={(rowData) => (
          <div
            style={{
              display: 'flex',
              flexDirection: 'row',
              alignItems: 'center',
            }}
          >
            <label style={{ fontWeight: 'bold' }}>
              {rowData.lastName} {rowData.firstName}
            </label>
          </div>
        )}
      />
      <Column
        style={{ textAlign: 'right' }}
        field="nationalId"
        header="ת.ז"
        body={(rowData: UserSummaryType) => <div>{rowData.nationalId}</div>}
      />
      <Column
        style={{ textAlign: 'right' }}
        field="phoneNumber"
        header="נייד"
        body={(rowData: UserSummaryType) => (
          <PhoneNumberComponent phoneNumber={rowData.phoneNumber} />
        )}
      />
      <Column
        style={{ textAlign: 'right' }}
        field="email"
        header="מייל"
        body={(rowData: UserSummaryType) => <div>{rowData.email}</div>}
      />
      <Column
        style={{ textAlign: 'right' }}
        body={(rowData: UserSummaryType) => (
          <Button
            icon="pi pi-trash"
            text
            severity="danger"
            tooltip={'הסרת סטודנט מהתוכנית'}
            loading={deleteStudentId === rowData.id}
            disabled={deleteStudentId !== null}
            onClick={async (e) => {
              e.stopPropagation();
              await handleStudentDelete(rowData.id, yearNumber);
            }}
          />
        )}
      />
    </DataTable>
  );

  const renderProgramStudentsTable = () => (
    <DataTable
      value={program.details?.programStudents || []}
      scrollable
      emptyMessage={
        <div style={{ textAlign: 'right' }}>לא נמצאו סטודנטים בתכנית</div>
      }
      dataKey="id"
      tableStyle={{
        direction: 'rtl',
        marginTop: '8px',
      }}
      onRowClick={(e) => handleUserClick(e.data as UserSummaryType)}
      className="clickable-rows"
    >
      <Column
        style={{ textAlign: 'right', width: '50px' }}
        field="lastName"
        header=""
        body={(rowData) => (
          <UserAvatarSmall
            imageUrl={rowData?.fileUrl}
            firstName={rowData?.firstName}
            lastName={rowData?.lastName}
          />
        )}
      />
      <Column
        style={{ textAlign: 'right', width: '150px' }}
        field="firstName"
        header="שם"
        body={(rowData) => (
          <div
            style={{
              display: 'flex',
              flexDirection: 'row',
              alignItems: 'center',
            }}
          >
            <label style={{ fontWeight: 'bold' }}>
              {rowData.lastName} {rowData.firstName}
            </label>
          </div>
        )}
      />
      <Column
        style={{ textAlign: 'right' }}
        field="nationalId"
        header="ת.ז"
        body={(rowData: ProgramStudent) => <div>{rowData.nationalId}</div>}
      />
      <Column
        style={{ textAlign: 'right' }}
        field="status"
        header="סטטוס"
        body={(rowData: ProgramStudent) => (
          <Button
            label={getProgramStudentStatusDisplayText(rowData.status)}
            className="p-button-text"
            onClick={() => handleStatusClick(rowData)}
          />
        )}
      />
      <Column
        style={{ textAlign: 'right' }}
        field="registrationDate"
        header="תאריך רישום"
        body={(rowData: ProgramStudent) => (
          <div>
            {!!rowData.registrationDate
              ? dateUtils.displayFormatDate(rowData.registrationDate)
              : 'טרם הוזן'}
          </div>
        )}
      />
      <Column
        style={{ textAlign: 'right' }}
        field="studyingStartDate"
        header="תאריך תחילת לימודים"
        body={(rowData: ProgramStudent) => (
          <div>
            {!!rowData.studyingStartDate
              ? dateUtils.displayFormatDate(rowData.studyingStartDate)
              : 'טרם הוזן'}
          </div>
        )}
      />
      {/*<Column
        style={{ textAlign: 'right' }}
        body={(rowData: ProgramStudent) => (
          <Button
            icon="pi pi-trash"
            text
            severity="danger"
            tooltip={'מחיקת סטודנט'}
            loading={deleteStudentId === rowData.id}
            disabled={deleteStudentId !== null}
            onClick={async (e) => {
              e.stopPropagation();
              await handleStudentDelete(rowData.id);
            }}
          />
        )}
      />*/}
    </DataTable>
  );

  // Get all courses from all years
  const allCourses =
    program.details?.years?.flatMap((year) => year.courses || []) || [];
  const unassignedCourses = program.details?.unAssignCourses || [];
  const combinedCourses = [...unassignedCourses, ...allCourses];

  return (
    <div className="program-students-container">
      <div
        style={{
          display: 'flex',
          flexDirection: 'row',
          gap: '16px',
          marginBottom: '16px',
          alignItems: 'center',
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          <Checkbox
            checked={showYearView}
            onChange={(e) => setShowYearView(e.checked || false)}
          />
          <label>הצג סטודנטים לפי שנים</label>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          <Checkbox
            checked={showProgramView}
            onChange={(e) => setShowProgramView(e.checked || false)}
          />
          <label>הצג סטודנטים בתכנית</label>
        </div>
      </div>

      {showYearView && (
        <Accordion multiple>
          {yearStudents.map((yearData) => (
            <AccordionTab
              key={yearData.year}
              header={
                <div
                  style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    width: '100%',
                  }}
                >
                  <div
                    style={{
                      display: 'flex',
                      flexDirection: 'row',
                      gap: '8px',
                      alignItems: 'center',
                    }}
                  >
                    <span>{yearData.yearName}</span>

                    {globalSettingsBranches.workingTimeManagement
                      ?.currentWorkingYear ===
                      yearData.year + program.metadata.programStartYear && (
                      <Tag
                        style={{
                          height: '22.5px',
                          display: 'flex',
                          justifyContent: 'center',
                          width: '90px',
                        }}
                      >
                        שנה נוכחית
                      </Tag>
                    )}
                  </div>
                  <div style={{ display: 'flex', gap: '8px' }}>
                    <Button
                      style={{
                        height: '30px',
                        width: '30px',
                        backgroundColor: '#0078d4',
                        borderColor: '#0078d4',
                      }}
                      icon="pi pi-envelope"
                      tooltip="ייצוא לאאוטלוק"
                      onClick={(e) => {
                        e.stopPropagation();
                        openOutlookDialog(
                          yearData.students,
                          `ייצוא לאאוטלוק - ${yearData.yearName}`,
                        );
                      }}
                    />
                    <Button
                      style={{
                        height: '30px',
                        width: '30px',
                        backgroundColor: '#10b981',
                        borderColor: '#10b981',
                      }}
                      icon="pi pi-file-excel"
                      tooltip="ייצוא לאקסל"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleExportToExcel(
                          yearData.students,
                          yearData.yearName,
                        );
                      }}
                    />
                    <Button
                      style={{ height: '30px', width: '30px' }}
                      icon="pi pi-plus"
                      tooltip="הוספת סטודנטים"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleAddStudentClick(yearData.year);
                      }}
                    />
                  </div>
                </div>
              }
            >
              {renderYearStudentsTable(yearData.students, yearData.year)}
            </AccordionTab>
          ))}
        </Accordion>
      )}

      {showProgramView && (
        <div style={{ marginTop: showYearView ? '16px' : '0' }}>
          <div
            style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              marginBottom: '8px',
            }}
          >
            <h2 style={{ textAlign: 'right', margin: 0 }}>סטודנטים בתכנית</h2>
            <div
              style={{
                display: 'flex',
                gap: '8px',
                alignItems: 'center',
                marginLeft: '20px',
              }}
            >
              <Button
                style={{
                  height: '30px',
                  width: '30px',
                  backgroundColor: '#0078d4',
                  borderColor: '#0078d4',
                }}
                icon="pi pi-envelope"
                tooltip="ייצוא לאאוטלוק"
                onClick={() =>
                  openOutlookDialog(
                    program.details?.programStudents || [],
                    `ייצוא לאאוטלוק - ${program.metadata?.programName || 'תוכנית'}`,
                  )
                }
              />
              <Button
                style={{
                  height: '30px',
                  width: '30px',
                  backgroundColor: '#10b981',
                  borderColor: '#10b981',
                }}
                icon="pi pi-file-excel"
                tooltip="ייצוא לאקסל"
                onClick={handleExportProgramStudentsToExcel}
              />
            </div>
          </div>
          {renderProgramStudentsTable()}
        </div>
      )}

      <AddStudentsDialog
        visible={showAddDialog}
        selectedYear={selectedYear}
        selectedUsers={selectedUsers}
        addCourses={addCourses}
        selectedCourses={selectedCourses}
        combinedCourses={combinedCourses}
        program={program.details!}
        onHide={() => {
          setShowAddDialog(false);
          setSelectedYear(null);
        }}
        onSelectedUsersChange={setSelectedUsers}
        onAddCoursesChange={setAddCourses}
        onSelectedCoursesChange={setSelectedCourses}
        onApprove={handleOnApprove}
      />

      <ProgramStudentStatusDialog
        visible={showStatusDialog}
        onHide={() => {
          setShowStatusDialog(false);
          setSelectedStudent(null);
        }}
        student={selectedStudent}
      />

      <UserPageDialog
        userRole={UserType.STUDENT}
        selectedUser={selectedUserForDialog}
        setSelectedUser={setSelectedUserForDialog}
        closeDialog={closeUserDialog}
        onHide={closeUserDialog}
      />

      <OutlookMailDialog
        visible={showOutlookDialog}
        onHide={() => setShowOutlookDialog(false)}
        users={outlookUsers}
        title={outlookTitle}
      />
    </div>
  );
});

export default ProgramStudentsTab;
